import React, { forwardRef, ReactNode } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { OutlinedInput, InputAdornment } from '@material-ui/core';
import { useDsTokens } from '../theme/useDsTokens';
import { cn } from '../utils/cn';
import { DsCommonProps, DsValidationProps, InputRef } from './types';

export interface InputProps extends DsCommonProps, DsValidationProps {
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'search';
  name?: string;
  readOnly?: boolean;
  fullWidth?: boolean;
  autoComplete?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
}

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
  },
}));

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    id,
    className,
    disabled,
    error,
    required,
    value,
    defaultValue,
    placeholder,
    type = 'text',
    name,
    readOnly,
    fullWidth = true,
    autoComplete,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    startAdornment,
    endAdornment,
    onChange,
    onBlur,
    onFocus,
    'data-testid': testId,
  },
  ref
) {
  const tokens = useDsTokens();
  const classes = useStyles();

  return (
    <OutlinedInput
      id={id}
      inputRef={ref}
      className={cn(classes.root, className)}
      data-testid={testId}
      value={value}
      defaultValue={defaultValue}
      placeholder={placeholder}
      type={type}
      name={name}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      fullWidth={fullWidth}
      autoComplete={autoComplete}
      error={error}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid ?? (error ? true : undefined)}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      startAdornment={
        startAdornment ? (
          <InputAdornment position="start">{startAdornment}</InputAdornment>
        ) : undefined
      }
      endAdornment={
        endAdornment ? (
          <InputAdornment position="end">{endAdornment}</InputAdornment>
        ) : undefined
      }
      style={{
        backgroundColor: readOnly ? tokens.surfaceSunken : tokens.surfaceInput,
        borderRadius: tokens.radiusMd,
        fontSize: tokens.fontSizeBody,
        color: tokens.textPrimary,
        minHeight: tokens.space5,
        transition: `border-color ${tokens.motionDurationStandard}ms ${tokens.motionEasingStandard}`,
      }}
      inputProps={{
        style: {
          padding: `10px ${tokens.space2}px`,
        },
      }}
    />
  );
});

Input.displayName = 'Input';

export type { InputRef };
